import { Component, computed, inject, ChangeDetectionStrategy } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormsModule } from "@angular/forms";
import { AccountType, CampaignType, StrategyStateEnum } from "@front/m19-api-client";
import { CAMPAIGN_TYPE, StrategyService } from "@front/m19-injection-token";
import { CampaignTypeWithTacos, Marketplaces, StrategyEx } from "@front/m19-models";
import { AccountSelectionService, AuthService, Constant } from "@front/m19-services";
import { IAlertComponent, IIconComponent, ModalService } from "@front/m19-ui";
import { TranslocoDirective } from "@jsverse/transloco";
import { OverdueAmazonInvoiceWarningComponent } from "@m19-board/settings/amazon-invoices/overdue-amazon-invoice-warning.component";
import { StrategyGridComponent } from "@m19-board/strategies/strategy-grid/strategy-grid.component";
import { map, switchMap } from "rxjs/operators";

export enum ViewTypeEnum {
  CARD = "CARD",
  LIST = "LIST",
}

export enum StrategyLimitWarning {
  NONE = "NONE",
  REACHED = "REACHED",
  SOON = "SOON",
}

@Component({
  selector: "app-strategy-list",
  templateUrl: "./strategy-list.component.html",
  imports: [
    IAlertComponent,
    FormsModule,
    TranslocoDirective,
    StrategyGridComponent,
    OverdueAmazonInvoiceWarningComponent,
    IIconComponent,
  ],
  providers: [ModalService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StrategyListComponent {
  private readonly accountSelection = inject(AccountSelectionService);
  private readonly authService = inject(AuthService);
  private readonly strategyService = inject(StrategyService);
  protected readonly campaignType = inject(CAMPAIGN_TYPE);

  readonly StrategyStateEnum = StrategyStateEnum;
  readonly CampaignType = CampaignType;
  readonly StrategyLimitWarning = StrategyLimitWarning;
  readonly StrategyEx = StrategyEx;
  readonly maxDspStrategies = Constant.maxDspStrategies;

  readonly locale = toSignal(this.authService.loggedUser$.pipe(map((user) => user?.locale)));
  readonly isReadOnly = toSignal(this.accountSelection.readOnlyMode$.pipe(map((b) => !!b)));
  readonly accountMarketplace = toSignal(this.accountSelection.singleAccountMarketplaceSelection$, {
    requireSync: true,
  });
  readonly marketplace = computed(() => Marketplaces[this.accountMarketplace().marketplace]);
  readonly isVendor = computed(() => this.accountMarketplace()?.accountType === AccountType.VENDOR);
  readonly isTacosAndVendor = computed(() => this.campaignType === CampaignTypeWithTacos.TACOS && this.isVendor());
  isMarketplaceSupportedForCampaignType = computed(() => {
    return (
      this.campaignType === CampaignTypeWithTacos.TACOS ||
      !this.marketplace().unsupportedCampaignTypes?.includes(this.campaignType)
    );
  });

  private readonly liveStrategies$ = this.accountSelection.singleAccountMarketplaceSelection$.pipe(
    switchMap((am) => this.strategyService.getStrategies(am.accountId, am.marketplace)),
    map((strategies) => Array.from(strategies.values())),
    map((strategies) => strategies.filter((x) => x.state === StrategyStateEnum.ENABLED)),
  );
  liveStrategies = toSignal(this.liveStrategies$);

  sbStrategyLimitWarning = computed(() => {
    if (this.campaignType != CampaignType.SB) {
      return StrategyLimitWarning.NONE;
    }
    const liveSBStrategies = this.liveStrategies()?.length;
    if (liveSBStrategies && liveSBStrategies >= Constant.maxSbStrategies) {
      return StrategyLimitWarning.REACHED;
    }
    if (liveSBStrategies && liveSBStrategies >= Constant.maxSbStrategies * 0.9) {
      return StrategyLimitWarning.SOON;
    }
    return StrategyLimitWarning.NONE;
  });
}
